import { useCallback, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';

import { getIsEVorHFC } from '../../../library/fitment/fitment-utils';
import {
  useAemContent,
  useGraphQLClient,
  useModal,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  useTreadwellPreferences
} from '../hooks';

import {
  GET_GEO_INFORMATION,
  VALIDATE_POSTAL_CODE
} from '../../graphql/form-queries';

import { trackAction } from '../../utils/analytics/analytics';
import { getShopTirePath } from '../../utils/fitment/fitment';
import { localStorage, sessionStorage } from '../../utils/storage/storage';
import { getIsAppBasedDriver } from '../../utils/treadwell/treadwell';
import { isValid5DigitPostalCode } from '../../utils/validators/validators';

import {
  AEM_CONTENT_IDS,
  DEFAULT_APP_BASED_DRIVER_TYPE,
  DEFAULT_FLEET_DRIVER_TYPE
} from '../../constants/cms';
import {
  APP_BASED_DRIVER,
  TREADWELL_MILES_DRIVEN_ANNUALLY,
  TREADWELL_SERVICE_DOWNTIME_COST
} from '../../constants/storage-keys';
import {
  DOWNTIME_COST_DEFAULT_VALUE,
  TREADWELL_CUSTOM_PERSONA_NAME,
  TREADWELL_DRIVING_PREFERENCES,
  TREADWELL_DRIVING_STYLES,
  TREADWELL_FORM_EVENTS
} from '../../constants/treadwell';

const DRIVING_RADIUS_MIN_VALUE = 1;
const DRIVING_RADIUS_MAX_VALUE = 255;

export default function useTreadwellForm(
  { isPackageFlow, onZipCodeSubmit } = {},
  fitment,
  isRecommendationAvailable
) {
  const initialZipCodeRef = useRef();
  const initialDrivingRadiusRef = useRef();
  const initialVehicleLoadRef = useRef();
  const client = useGraphQLClient();
  const navigateTo = useNavigateTo();
  const { closeModal } = useModal();
  const { isFleetAccount } = useMyAccount();

  const {
    defaultMilesDrivenAnnually,
    isTreadwellFlow,
    saveTreadwellPreferences,
    treadwellPreferences
  } = useTreadwellPreferences();

  const [isDirty, setIsDirty] = useState(false);
  const [zipCodeError, setZipCodeError] = useState({
    hasErrors: false,
    message: ''
  });
  const [zipCodeValue, setZipCodeValue] = useState('');
  const [drivingRadiusValue, setDrivingRadiusValue] = useState('');
  const [zipCodeWeatherData, setZipCodeWeatherData] = useState(null);
  const [vehicleLoad, setVehicleLoad] = useState('');
  const [isCustomDriverTypeShowing, setIsCustomDriverTypeShowing] =
    useState(false);
  const [selectedDriverType, setSelectedDriverType] = useState(null);
  const [selectedDrivingPreferences, setSelectedDrivingPreferences] = useState(
    {}
  );
  const [geoServiceData, setGeoServiceData] = useState({});
  const [hasGeoServiceError, setHasGeoServiceError] = useState(false);
  const [isGeoServiceDataLoading, setIsGeoServiceDataLoading] = useState(false);
  const [selectedCustomDrivingPriorities, setSelectedCustomDrivingPriorities] =
    useState(treadwellPreferences?.selectedCustomDrivingPriorities || null);
  const [
    selectedCustomDrivingPrioritiesType,
    setSelectedCustomDrivingPrioritiesType
  ] = useState(
    treadwellPreferences?.selectedCustomDrivingPrioritiesType ||
      TREADWELL_CUSTOM_PERSONA_NAME
  );
  const [showInStockTires, setShowInStockTires] = useState(false);
  const [showOffRoadTires, setShowOffRoadTires] = useState(
    treadwellPreferences?.showOffRoadTires || false
  );
  const [showWinterTires, setShowWinterTires] = useState(
    treadwellPreferences?.showWinterTires || false
  );

  const isZipCodeValid =
    zipCodeValue?.length === 5 && Boolean(zipCodeWeatherData);

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const { aemContent } = useAemContent(AEM_CONTENT_IDS.treadwellPersonas);
  const isAppBasedDriver = getIsAppBasedDriver();
  const isFleetOrAppBasedDriver = isFleetTreadwell || isAppBasedDriver;
  const appBasedDriver = sessionStorage(APP_BASED_DRIVER);
  const isEVorHFC = getIsEVorHFC(fitment);

  const { myStore } = useMyStore();
  const myStorePostalCode = myStore?.address?.postalCode?.slice?.(0, 5);
  const myStoreCity = myStore?.address?.town || '';
  const myStoreState = myStore?.address?.region?.isocodeShort || '';

  const isDrivingRadiusValid = useMemo(
    () =>
      /^\d+(\.\d{1,2})?$/.test(drivingRadiusValue) &&
      drivingRadiusValue >= DRIVING_RADIUS_MIN_VALUE &&
      drivingRadiusValue <= DRIVING_RADIUS_MAX_VALUE,
    [drivingRadiusValue]
  );

  const isDrivingPreferencesValid = useMemo(
    () =>
      Object.values(selectedDrivingPreferences).every(val =>
        /^\d+(\.\d{1,2})?$/.test(val)
      ),
    [selectedDrivingPreferences]
  );

  const isDrivingLocationFormValid = useMemo(() => {
    if (isFleetOrAppBasedDriver) {
      return isZipCodeValid && isDrivingRadiusValid;
    }

    return isZipCodeValid;
  }, [isFleetOrAppBasedDriver, isDrivingRadiusValid, isZipCodeValid]);

  const onDrivingRadiusChange = useCallback(e => {
    const input = e.target.value;

    if (input === '') {
      setDrivingRadiusValue('');
      return;
    }

    // regex represents numbers up to 2 decimal places
    if (!/^\d*\.?\d{0,2}$/.test(input)) return;

    const number = Number(input);
    if (
      number < DRIVING_RADIUS_MIN_VALUE ||
      number > DRIVING_RADIUS_MAX_VALUE
    ) {
      return;
    }

    setDrivingRadiusValue(input);

    if (input !== initialDrivingRadiusRef.current) {
      setSelectedDrivingPreferences({});
    }
  }, []);

  const onDriverTypeSelected = useCallback(driverType => {
    setIsDirty(true);
    setSelectedCustomDrivingPrioritiesType(TREADWELL_CUSTOM_PERSONA_NAME);
    setSelectedDriverType(driverType);
  }, []);

  const onSelectedCustomDrivingPriorities = useCallback(priorities => {
    setIsDirty(true);
    setSelectedCustomDrivingPriorities(priorities);
  }, []);

  const onSelectedCustomDrivingPrioritiesType = useCallback(priorityType => {
    setIsDirty(true);
    setSelectedCustomDrivingPrioritiesType(priorityType);
  }, []);

  const onUpdateShowInStockTires = useCallback(
    () => setShowInStockTires(!showInStockTires),
    [showInStockTires, setShowInStockTires]
  );

  const onUpdateShowOffRoadTires = useCallback(
    () => setShowOffRoadTires(!showOffRoadTires),
    [showOffRoadTires, setShowOffRoadTires]
  );

  const onUpdateShowWinterTires = useCallback(
    () => setShowWinterTires(!showWinterTires),
    [showWinterTires, setShowWinterTires]
  );

  const onSubmitTreadwellPreferences = useCallback(() => {
    if (
      zipCodeError.hasErrors ||
      (!isCustomDriverTypeShowing &&
        !selectedDriverType &&
        !isFleetOrAppBasedDriver)
    ) {
      return;
    }

    const preferencesToSave = {
      isCustomDriverTypeShowing,
      selectedCustomDrivingPriorities: null,
      selectedCustomDrivingPrioritiesType: null,
      selectedDriverType: null,
      selectedInStockTires: null,
      showOffRoadTires: showOffRoadTires,
      showWinterTires: showWinterTires,
      timestamp: Date.now(),
      zipCodeValue,
      zipCodeWeatherData
    };

    if (isCustomDriverTypeShowing) {
      preferencesToSave.selectedCustomDrivingPriorities =
        selectedCustomDrivingPriorities;
      preferencesToSave.selectedCustomDrivingPrioritiesType =
        TREADWELL_CUSTOM_PERSONA_NAME;
    } else {
      preferencesToSave.selectedDriverType = selectedDriverType;
      preferencesToSave.selectedCustomDrivingPriorities =
        selectedCustomDrivingPriorities;
      preferencesToSave.selectedCustomDrivingPrioritiesType =
        TREADWELL_CUSTOM_PERSONA_NAME;
    }
    preferencesToSave.selectedInStockTires = showInStockTires;

    if (isFleetOrAppBasedDriver) {
      preferencesToSave.drivingRadius = drivingRadiusValue;

      preferencesToSave.selectedDrivingPreferences = selectedDrivingPreferences;

      if (isFleetTreadwell) {
        preferencesToSave.vehicleLoad = vehicleLoad;
      }
    }

    saveTreadwellPreferences(preferencesToSave);
  }, [
    drivingRadiusValue,
    isCustomDriverTypeShowing,
    isFleetOrAppBasedDriver,
    isFleetTreadwell,
    saveTreadwellPreferences,
    selectedCustomDrivingPriorities,
    selectedDriverType,
    selectedDrivingPreferences,
    showInStockTires,
    showOffRoadTires,
    showWinterTires,
    vehicleLoad,
    zipCodeError.hasErrors,
    zipCodeValue,
    zipCodeWeatherData
  ]);

  const onShowPLP = useCallback(() => {
    closeModal();

    let query;

    if (isTreadwellFlow && isRecommendationAvailable === false) {
      query = '?q=%3AbestSeller-asc&sort=bestSeller-asc';
    } else {
      const baseQuery = isFleetEnabled ? '?q=' : '?q=%3Arecommended';
      const sortParam = isFleetEnabled ? '' : '&sort=recommended';
      query = `${baseQuery}${
        showOffRoadTires
          ? '%3AtireCategory%3AmudTerrainTires%3AtireCategory%3AallTerrainTires'
          : ''
      }${
        showInStockTires
          ? `%3AstoreInStock%3AIn%20Stock${sortParam}`
          : sortParam
      }&currentSearchOption=treadwell`;
    }

    navigateTo(getShopTirePath(fitment, isPackageFlow) + query);
  }, [
    closeModal,
    showInStockTires,
    showOffRoadTires,
    isFleetEnabled,
    isTreadwellFlow,
    isRecommendationAvailable,
    navigateTo,
    fitment,
    isPackageFlow
  ]);

  const onToggleIsCustomDriverTypeShowing = useCallback(() => {
    setIsDirty(true);
    setIsCustomDriverTypeShowing(!isCustomDriverTypeShowing);
  }, [isCustomDriverTypeShowing]);

  const onDrivingPreferencesChange = useCallback((preference, value) => {
    if (value === '') {
      setSelectedDrivingPreferences(prev => ({
        ...prev,
        [preference]: ''
      }));
      return false;
    }

    if (preference === TREADWELL_DRIVING_PREFERENCES.VEHICLE_FUEL_MILEAGE) {
      if (!/^\d*\.?\d{0,2}$/.test(value)) return;
      if (value < 1 || value > 255) {
        return false;
      }
    }

    if (preference === TREADWELL_DRIVING_PREFERENCES.FUEL_COST) {
      if (!/^\d{0,5}(\.\d{0,2})?$/.test(value)) return;
      if (value < 1 || value > 99999.99) {
        return false;
      }
    }

    if (preference === TREADWELL_DRIVING_PREFERENCES.MILES_DRIVEN_ANNUALLY) {
      if (!/^\d{0,6}$/.test(value)) {
        return false;
      }
    }

    if (preference === TREADWELL_DRIVING_PREFERENCES.DOWNTIME_COST) {
      if (!/^\d{0,5}$/.test(value)) return;
      if (value < 1 || value > 99999.99) {
        return false;
      }
    }

    setSelectedDrivingPreferences(prev => ({
      ...prev,
      [preference]: value
    }));

    return true;
  }, []);

  const onZipCodeChange = useCallback(
    e => {
      const value = e.target.value;

      if (!/^[0-9]{0,5}$/.test(value)) {
        e.preventDefault?.();
        e.stopPropagation?.();
        return;
      }
      setIsDirty(true);
      setZipCodeValue(value ?? '');
      setZipCodeWeatherData(null);
      setZipCodeError({
        hasErrors: false,
        message: ''
      });

      if (isValid5DigitPostalCode(value)) {
        if (value !== initialZipCodeRef.current) {
          setSelectedDrivingPreferences({});
        }

        client
          .query({
            query: VALIDATE_POSTAL_CODE,
            variables: { postalCode: value?.toUpperCase() }
          })
          .then(({ data }) => {
            if (data?.contactValidation?.postalCode) {
              const { city, isValid, state } =
                data.contactValidation.postalCode;

              if (isValid) {
                setZipCodeWeatherData({
                  city,
                  state
                });
                return;
              }
              setZipCodeError({
                hasErrors: true,
                message: 'unable to find weather data for recommendation'
              });
            }
          })
          .catch(e => {
            console.error('could not fetch treadwell zip code data', e);

            setZipCodeError({
              hasErrors: true,
              message: 'unable to find weather data for recommendation'
            });
          });
      }
    },
    [client]
  );

  const onZipCodeKeyPress = useCallback(
    e => {
      if (isDrivingLocationFormValid && e.key === 'Enter' && isZipCodeValid) {
        e.preventDefault?.();
        e.stopPropagation?.();
        onZipCodeSubmit?.();
        return true;
      }
      return false;
    },
    [isDrivingLocationFormValid, isZipCodeValid, onZipCodeSubmit]
  );

  const initializeZipCodeValue = useCallback(() => {
    setZipCodeWeatherData({
      city: myStoreCity,
      state: myStoreState
    });
    setIsDirty(false);

    let value = '';
    if (treadwellPreferences?.zipCodeValue) {
      value = treadwellPreferences.zipCodeValue;

      if (
        treadwellPreferences.zipCodeWeatherData?.city &&
        treadwellPreferences.zipCodeWeatherData?.state
      ) {
        setZipCodeWeatherData({
          city: treadwellPreferences.zipCodeWeatherData.city,
          state: treadwellPreferences.zipCodeWeatherData.state
        });
      }
    } else {
      value = zipCodeValue || myStorePostalCode;
      setZipCodeError({
        hasErrors: false,
        message: ''
      });
    }

    setZipCodeValue(value);
    initialZipCodeRef.current = value;
  }, [
    myStoreCity,
    myStorePostalCode,
    myStoreState,
    treadwellPreferences,
    zipCodeValue
  ]);

  const initializeDrivingRadiusValue = useCallback(() => {
    let value = '';
    if (treadwellPreferences) {
      value = treadwellPreferences.drivingRadius;
    } else {
      value = drivingRadiusValue;
    }

    setDrivingRadiusValue(value);
    initialDrivingRadiusRef.current = value;
  }, [drivingRadiusValue, treadwellPreferences]);

  const initializeVehicleLoadValue = useCallback(() => {
    let value = '';
    if (treadwellPreferences?.vehicleLoad) {
      value = treadwellPreferences.vehicleLoad;
    } else {
      value = vehicleLoad || TREADWELL_DRIVING_STYLES.STANDARD_DUTY;
    }

    setVehicleLoad(value);
    initialVehicleLoadRef.current = value;
  }, [treadwellPreferences, vehicleLoad]);

  const initializeSelectedDrivingType = useCallback(() => {
    setIsDirty(false);
    if (treadwellPreferences) {
      setIsCustomDriverTypeShowing(
        treadwellPreferences.isCustomDriverTypeShowing
      );

      setSelectedDriverType(treadwellPreferences.selectedDriverType);

      if (treadwellPreferences.selectedCustomDrivingPrioritiesType) {
        setSelectedCustomDrivingPrioritiesType(
          treadwellPreferences.selectedCustomDrivingPrioritiesType
        );
      }
    } else {
      setIsCustomDriverTypeShowing(false);
      setSelectedDriverType(null);
      setSelectedCustomDrivingPriorities(null);
      setSelectedCustomDrivingPrioritiesType(TREADWELL_CUSTOM_PERSONA_NAME);
    }
  }, [treadwellPreferences]);

  const hasDrivingLocationFormChanged = useMemo(
    () =>
      (initialZipCodeRef.current &&
        initialZipCodeRef.current !== zipCodeValue) ||
      (initialDrivingRadiusRef.current &&
        initialDrivingRadiusRef.current !== drivingRadiusValue),
    [drivingRadiusValue, zipCodeValue]
  );
  const initializeSelectedDrivingPreferences = useCallback(async () => {
    setIsDirty(false);
    if (isFleetOrAppBasedDriver) {
      const { selectedDrivingPreferences: storedSelectedDrivingPreferences } =
        treadwellPreferences || {};
      const storedDowntimeCost = localStorage(TREADWELL_SERVICE_DOWNTIME_COST);
      const storedMilesDrivenAnnually = localStorage(
        TREADWELL_MILES_DRIVEN_ANNUALLY
      );
      setIsGeoServiceDataLoading(true);
      const variables = {
        request: {
          drivingPostalCode: zipCodeValue,
          fuelType: fitment?.fuelType,
          radiusMiles: drivingRadiusValue,
          storeCode: myStore?.code,
          vehicleId: fitment?.vehicleId,
          vehicleMake: fitment?.make,
          vehicleYear: fitment?.year
        }
      };
      const { data, errors } = await client.query({
        fetchPolicy: 'network-only',
        query: GET_GEO_INFORMATION,
        variables
      });

      const geoResponse = data?.recommendationQueries?.getGeoInformation;
      if (data && !errors) {
        setGeoServiceData(geoResponse);
        setHasGeoServiceError(false);
        setIsGeoServiceDataLoading(false);
      } else {
        trackAction('treadwell_driving_preferences_load_error', {
          error_reason: errors?.[0]?.extensions?.classification
        });
        setHasGeoServiceError(true);
        setIsGeoServiceDataLoading(false);
        return false;
      }
      onDriverTypeSelected(
        isFleetTreadwell
          ? DEFAULT_FLEET_DRIVER_TYPE
          : aemContent?.[appBasedDriver] || DEFAULT_APP_BASED_DRIVER_TYPE
      );

      if (isEmpty(selectedDrivingPreferences)) {
        const hasAllDrivingPreferenceValues = Boolean(
          storedSelectedDrivingPreferences?.vehicleFuelMileage &&
            storedSelectedDrivingPreferences?.fuelCost &&
            storedSelectedDrivingPreferences?.milesDrivenAnnually &&
            (!isFleetTreadwell || storedDowntimeCost)
        );
        if (hasAllDrivingPreferenceValues && !hasDrivingLocationFormChanged) {
          setSelectedDrivingPreferences({
            ...storedSelectedDrivingPreferences,
            milesDrivenAnnually: storedMilesDrivenAnnually,
            ...(isFleetTreadwell && {
              downtimeCost: storedDowntimeCost
            })
          });
        } else {
          setSelectedDrivingPreferences({
            [TREADWELL_DRIVING_PREFERENCES.FUEL_COST]: isEVorHFC
              ? geoResponse?.averagePrice?.commercialKw
              : geoResponse?.averagePrice?.regularGas,
            [TREADWELL_DRIVING_PREFERENCES.MILES_DRIVEN_ANNUALLY]:
              storedMilesDrivenAnnually ||
              storedSelectedDrivingPreferences?.milesDrivenAnnually ||
              defaultMilesDrivenAnnually,
            ...(isFleetTreadwell && {
              downtimeCost: storedDowntimeCost || DOWNTIME_COST_DEFAULT_VALUE
            }),
            [TREADWELL_DRIVING_PREFERENCES.VEHICLE_FUEL_MILEAGE]:
              geoResponse?.mpgk,
            [TREADWELL_DRIVING_PREFERENCES.RADIUS_MILES]: drivingRadiusValue
          });
        }
      }
      setHasGeoServiceError(false);
      setIsGeoServiceDataLoading(false);
      return true;
    }
  }, [
    aemContent,
    appBasedDriver,
    client,
    defaultMilesDrivenAnnually,
    drivingRadiusValue,
    fitment?.fuelType,
    fitment?.make,
    fitment?.vehicleId,
    fitment?.year,
    hasDrivingLocationFormChanged,
    isFleetOrAppBasedDriver,
    isFleetTreadwell,
    isEVorHFC,
    myStore?.code,
    onDriverTypeSelected,
    selectedDrivingPreferences,
    treadwellPreferences,
    zipCodeValue
  ]);

  const onEvent = useCallback(
    event => {
      switch (event) {
        case TREADWELL_FORM_EVENTS.INITIALIZE_DRIVER_TYPE_FORM: {
          initializeSelectedDrivingType();
          break;
        }

        case TREADWELL_FORM_EVENTS.INITIALIZE_DRIVING_LOCATION_FORM: {
          initializeZipCodeValue();
          initializeDrivingRadiusValue();
          break;
        }

        case TREADWELL_FORM_EVENTS.INITIALIZE_VEHICLE_LOAD_FORM: {
          initializeVehicleLoadValue();
          break;
        }

        case TREADWELL_FORM_EVENTS.CLEAR_TREADWELL_FORM: {
          setIsDirty(false);
          setZipCodeValue('');
          setSelectedDrivingPreferences({});
          setHasGeoServiceError(false);
          setIsGeoServiceDataLoading(false);
          setDrivingRadiusValue('');
          setZipCodeWeatherData(null);
          setZipCodeError({
            hasErrors: false,
            message: ''
          });
          setVehicleLoad('');
          setIsCustomDriverTypeShowing(false);
          setSelectedDriverType(null);
          setSelectedCustomDrivingPriorities(null);
          setSelectedCustomDrivingPrioritiesType(TREADWELL_CUSTOM_PERSONA_NAME);
          break;
        }

        default:
          break;
      }
    },
    [
      initializeDrivingRadiusValue,
      initializeSelectedDrivingType,
      initializeVehicleLoadValue,
      initializeZipCodeValue
    ]
  );

  const resetFormStates = useCallback(() => {
    onEvent(TREADWELL_FORM_EVENTS.CLEAR_TREADWELL_FORM);
  }, [onEvent]);

  return {
    drivingRadiusValue,
    geoServiceData,
    hasGeoServiceError,
    initializeSelectedDrivingPreferences,
    isCustomDriverTypeShowing,
    isDirty,
    isDrivingLocationFormValid,
    isDrivingPreferencesValid,
    isGeoServiceDataLoading,
    isValid:
      isZipCodeValid &&
      (isCustomDriverTypeShowing || Boolean(selectedDriverType)),
    isZipCodeValid,
    onCancel: resetFormStates,
    onDriverTypeSelected,
    onDrivingPreferencesChange,
    onDrivingRadiusChange,
    onEvent,
    onSelectedCustomDrivingPriorities,
    onSelectedCustomDrivingPrioritiesType,
    onShowPLP,
    onSubmitTreadwellPreferences,
    onToggleIsCustomDriverTypeShowing,
    onUpdateShowInStockTires,
    onUpdateShowOffRoadTires,
    onUpdateShowWinterTires,
    onVehicleLoadChange: setVehicleLoad,
    onZipCodeChange,
    onZipCodeKeyPress,
    selectedCustomDrivingPriorities,
    selectedCustomDrivingPrioritiesType,
    selectedDriverType,
    selectedDrivingPreferences,
    showDrivingRadius: isFleetOrAppBasedDriver,
    showInStockTires,
    showOffRoadTires,
    showWinterTires,
    vehicleLoad,
    zipCodeError,
    zipCodeValue,
    zipCodeWeatherData
  };
}
